// IBAN parsing and validation.
//
// Kept apart from the page so it can be exercised without a browser - it is the
// only part of the front end with logic worth testing rather than wiring. Loads
// as a plain script in the browser (defining globals) and as a module under
// Node, so tests/js/ can require it directly with no bundler.
(function (global) {
  "use strict";

  // Per supported country: total length, and the slice holding the bank code.
  const ibanFormat = {
    HR: { length: 21, vbdiFrom: 4, vbdiTo: 11 },
    LT: { length: 20, vbdiFrom: 4, vbdiTo: 8 },
  };

  const UNSUPPORTED_BANK = "Banka nije podržana";

  function normaliseIban(value) {
    return String(value || "").replace(/\s/g, "").toUpperCase();
  }

  // ISO 13616 mod-97, digit by digit so no BigInt is needed.
  function ibanChecksumValid(iban) {
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const character of rearranged) {
      const value =
        character >= "0" && character <= "9"
          ? character
          : String(character.charCodeAt(0) - 55);
      for (const digit of value) {
        remainder = (remainder * 10 + Number(digit)) % 97;
      }
    }
    return remainder === 1;
  }

  // Whether the checksum should be enforced. The server decides: 9 of the 17
  // IBANs the banks publish for their own sandboxes fail mod-97, so enforcing
  // it there would reject the banks' own test data. See validation_rules() in
  // app/routes.py.
  function checksumEnforced(options) {
    if (options && typeof options.checksum === "boolean") {
      return options.checksum;
    }
    return Boolean((global.VALIDATION || {}).ibanChecksum);
  }

  // What is wrong with this IBAN, in Croatian, or null when it is usable.
  function ibanProblem(value, options) {
    const iban = normaliseIban(value);
    if (!iban) return "Unesite IBAN.";
    if (!/^[A-Z]{2}[0-9A-Z]+$/.test(iban)) {
      return "IBAN sadrži nedozvoljene znakove.";
    }

    const format = ibanFormat[iban.slice(0, 2)];
    if (!format) return "Podržani su samo hrvatski (HR) i litavski (LT) IBAN-i.";
    if (iban.length !== format.length) {
      return `IBAN mora imati ${format.length} znakova, a ima ${iban.length}.`;
    }
    if (checksumEnforced(options) && !ibanChecksumValid(iban)) {
      return "Kontrolni broj IBAN-a nije ispravan.";
    }
    return null;
  }

  function validateIban(iban, options) {
    return ibanProblem(iban, options) === null;
  }

  // The bank this IBAN belongs to, or null while there is not yet enough typed
  // to say. The vbdi -> name map comes from the server, so the bank registry
  // stays the single source of truth.
  function bankNameForIban(iban, banksByVbdi) {
    const clean = normaliseIban(iban);
    const format = ibanFormat[clean.slice(0, 2)];
    if (!format) return null;

    const vbdi = clean.slice(format.vbdiFrom, format.vbdiTo);
    if (vbdi.length < format.vbdiTo - format.vbdiFrom) return null;

    const known = banksByVbdi || global.BANKS_BY_VBDI || {};
    if (known[vbdi]) return known[vbdi];

    // Only once the whole IBAN is typed do we call the bank unsupported.
    return clean.length >= format.length ? UNSUPPORTED_BANK : null;
  }

  const api = {
    ibanFormat,
    UNSUPPORTED_BANK,
    normaliseIban,
    ibanChecksumValid,
    ibanProblem,
    validateIban,
    bankNameForIban,
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
  Object.assign(global, api);
})(typeof window !== "undefined" ? window : globalThis);
